import { FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/Form/Form';
import { PasswordInput } from '@/components/ui/PasswordInput/PasswordInput';
import { PasswordRequirement } from '@/components/ui/PasswordRequirement/PasswordRequirement';
import type { Control, FieldValues, Path } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { validatePassword } from './validation';

export interface PasswordFormFieldProps<TFieldValues extends FieldValues> {
  control: Control<TFieldValues>;
  name: Path<TFieldValues>;
  autoComplete?: string;
  revealButton?: {
    showAriaLabel?: string;
    hideAriaLabel?: string;
  };
  requirementsAriaLabel?: string;
  label?: string;
}

export const PasswordFormField = <TFieldValues extends FieldValues>({
  control,
  name,
  autoComplete,
  revealButton,
  requirementsAriaLabel,
  label,
}: PasswordFormFieldProps<TFieldValues>) => {
  const { t } = useTranslation('components', {
    keyPrefix: 'passwordFormField',
  });

  return (
    <FormField
      control={control}
      name={name}
      render={({ field, formState }) => {
        const passwordValue = field.value || '';
        const wasFormSubmitted = formState.isSubmitted;

        const { isMinLength, hasUppercase, hasLowercase, hasNumber, hasSpecialChar, isValid } = validatePassword(passwordValue);

        const requirements = [
          { key: 'requirement-min-length', isMet: isMinLength, description: t('requirements.length') },
          { key: 'requirement-has-uppercase', isMet: hasUppercase, description: t('requirements.uppercase') },
          { key: 'requirement-has-lowercase', isMet: hasLowercase, description: t('requirements.lowercase') },
          { key: 'requirement-has-number', isMet: hasNumber, description: t('requirements.number') },
          { key: 'requirement-has-special-char', isMet: hasSpecialChar, description: t('requirements.specialChar') },
        ];

        const metRequirementsCount = requirements.filter((req) => req.isMet).length;

        const missingRequirementsDescription = requirements
          .filter((req) => !req.isMet)
          .map((req) => req.description)
          .join(', ');

        return (
          <FormItem>
            <FormLabel>{label ?? t('label')}</FormLabel>
            <FormControl>
              <PasswordInput autoComplete={autoComplete} {...field} revealButton={revealButton} />
            </FormControl>
            <FormDescription asChild>
              <div role='region' aria-label={requirementsAriaLabel ?? t('requirements.ariaLabel')}>
                <ul className='flex flex-col gap-1 p-2'>
                  {requirements.map((req) => (
                    <PasswordRequirement key={req.key} isMet={req.isMet} shouldHighlight={wasFormSubmitted}>
                      {req.description}
                    </PasswordRequirement>
                  ))}
                </ul>
                <span className='sr-only' aria-live='polite'>
                  {isValid
                    ? t('accessibleText.allRequirementsMet')
                    : t('accessibleText.someRequirementsNotMet', {
                        metRequirementsCount,
                        missingRequirementsDescription,
                      })}
                </span>
              </div>
            </FormDescription>
            <FormMessage />
          </FormItem>
        );
      }}
    />
  );
};
